import { injectable, inject } from 'inversify';
import { Message, Field, Type } from "protobufjs/light";
import { QuestionStateProtobufSerializer } from '@/app/question/tracking/serialization'
import { QuizSessionQuestionStatesState, QuizSessionQuestionState, QuestionPhase } from '../../../api'

@injectable()
export class QuizSessionQuestionStatesProtobufMapper {

  constructor(
    @inject(QuestionStateProtobufSerializer)
    private contentSerializer: QuestionStateProtobufSerializer) {
  }

  map(input: QuizSessionQuestionStatesState): QuizSessionQuestionStateProtoMessage[] {
    return input.map(item => {
      if (item === null) {
        return new QuizSessionQuestionStateProtoMessage()
      }

      return this.mapItem(item)
    })
  }

  private mapItem(input: QuizSessionQuestionState) {
    const stateResult = this.contentSerializer.serializeContent(input.type, 'state', input.state)
    const answerResult = this.contentSerializer.serializeContent(input.type, 'answer', input.answer)
    if (!stateResult.success || !answerResult.success) {
      return new QuizSessionQuestionStateProtoMessage()
    }
    
    return new QuizSessionQuestionStateProtoMessage({
      type: input.type,
      state: stateResult.value,
      answer: answerResult.value,
      phase: mapPhase(input.phase),
      score: input.result.score
    })

    function mapPhase(phase: QuestionPhase): QuizSessionQuestionPhaseProtoEnum {
      switch (phase) {
        case 'not-answered':
          return QuizSessionQuestionPhaseProtoEnum.none
        case 'answered':
          return QuizSessionQuestionPhaseProtoEnum.answered
        case 'evaluated':
          return QuizSessionQuestionPhaseProtoEnum.evaluated
        case 'solution-shown':
          return QuizSessionQuestionPhaseProtoEnum.solutionShown
      }
    }
  }
  
  unmap(input: QuizSessionQuestionStateProtoMessage[]): QuizSessionQuestionStatesState {
    return input.map(item => {
      return this.unmapItem(item)
    })
  }

  private unmapItem(input: QuizSessionQuestionStateProtoMessage): QuizSessionQuestionState | null {

    const state = this.contentSerializer.deserializeContent(input.type, 'state', input.state)
    const answer = this.contentSerializer.deserializeContent(input.type, 'answer', input.answer)

    if (!state.success || !answer.success) {
      return null
    }

    const phase = unmapPhase(input.phase)
    if (phase === 'not-answered' && state.value === null) {
      // we have no answer and no state
      return null
    }

    const result: QuizSessionQuestionState = {
      type: input.type,
      state: state.value,
      answer: answer.value,
      phase: phase,
      result: {
        score: input.score
      }
    }

    return result

    function unmapPhase(input: QuizSessionQuestionPhaseProtoEnum): QuestionPhase {
      switch (input) {
        case QuizSessionQuestionPhaseProtoEnum.none:
          return 'not-answered'
        case QuizSessionQuestionPhaseProtoEnum.answered:
          return 'answered'
        case QuizSessionQuestionPhaseProtoEnum.evaluated:
          return 'evaluated'
        case QuizSessionQuestionPhaseProtoEnum.solutionShown:
          return 'solution-shown'
      }
    }
  }
}

enum QuizSessionQuestionPhaseProtoEnum {
  none = 0,
  answered = 1,
  evaluated = 2,
  solutionShown = 3
}

@Type.d('QuizSessionQuestionStateProtoMessage')
export class QuizSessionQuestionStateProtoMessage extends Message<QuizSessionQuestionStateProtoMessage> {

  @Field.d(1, 'string', "optional", '')
  type: string

  @Field.d(2, 'bytes', "optional", [])
  answer: Uint8Array

  @Field.d(4, 'float', "optional", 0)
  score: number

  @Field.d(5, QuizSessionQuestionPhaseProtoEnum, "optional", QuizSessionQuestionPhaseProtoEnum.none)
  phase: QuizSessionQuestionPhaseProtoEnum

  @Field.d(6, 'bytes', "optional", [])
  state: Uint8Array
}
